//shared containers
import { Footer } from '@/containers-m/Footer';
import { Navigation } from '/containers-m/';

//containers
import {
  Hero,
  WatchItRunYourFreight,
  HowItWorks,
  RunsOnYourStack,
  TheRestOfTheLineup,
  EndCTA,
} from '../containers';

//styles
import {
  PageWrapper,
  YardTaskOrchestrationGlobalStyle,
} from './YardTaskOrchestration.styles';

const hero = {
  slug: 'yard-task-orchestration',
  title: 'Yard Task Orchestration',
  description:
    'Creates and assigns yard tasks in the right order when an event calls for one, such as a truck arriving after hours or a container sitting too long, and tells suppliers and carriers when something needs their attention.',
  metric: '20–30%',
  metricLabel: 'higher yard labor productivity',
};

const watchItRun = {
  title: 'A reefer trailer arrived after hours',
  stageHeadTitle: 'A reefer trailer just checked in at 2am, with no door until 6.',
  stageHeadMeta: 'Yard · Trailer 53-1187',
  rows: [
    {
      time: '02:14:00',
      text: 'Reefer trailer checked in after hours. No door until 6am.',
    },
    {
      time: '02:14:05',
      text: 'Move to a powered reefer spot created and assigned to the night yard driver.',
    },
    {
      time: '02:31:40',
      text: 'Move confirmed. Temperature check scheduled for 4am.',
    },
    {
      time: '04:06:12',
      text: 'Temperature logged and inside the load’s range.',
    },
    {
      time: '05:52:30',
      text: 'Trailer spotted at Door 9 for the first shift. Every task done in order.',
    },
  ],
  summaryText:
    'A temperature-controlled trailer arrived with no door open until morning. Loadsmart AI created the tasks it needed in the right order, a move to a powered spot, a temperature check and a spot at the door for the first shift, and confirmed each one as the yard driver completed it.',
  agentBadge: 'Yard Agent',
  escalationTitle: 'Anything off-plan goes to a person.',
  escalationText:
    'When a task can’t be done as planned, such as a temperature out of range, a failed inspection or no open spot, the agent stops the sequence and sends it to your yard lead with the trailer, the task and what was found.',
};

const howItWorks = {
  stepsTitleWhite: 'Creates the right yard task at the right time,',
  stepsTitleGradient: 'and keeps the work in sequence.',
  steps: [
    {
      title: 'Watches yard events',
      description:
        'Reads gate check-ins, appointments, door availability and how long each trailer has been in the yard.',
    },
    {
      title: 'Creates the task',
      description:
        'Creates the move, temperature check or quality inspection an event calls for, such as a truck arriving after hours or a container sitting too long.',
    },
    {
      title: 'Assigns and sequences',
      description:
        'Assigns each task to a yard driver in the right order, and confirms it when it’s done.',
    },
    {
      title: 'Notifies suppliers and carriers',
      description:
        'Tells them when an event needs their attention, such as an empty container waiting to be picked up.',
    },
  ],
  outcomes: [
    {
      title: 'Yard drivers always know what’s next',
      description:
        'Each driver works from a queue in the right order, so nobody waits for instructions.',
    },
    {
      title: 'Checks happen every time',
      description:
        'Temperature checks and inspections are created on schedule, so none get skipped on a busy shift.',
    },
    {
      title: 'Containers don’t sit forgotten',
      description:
        'Long-dwell containers are flagged and their carriers told before they become a problem.',
    },
  ],
};

const lineupCards = [
  {
    slug: 'dock-door-management',
    title: 'Dock Door Management',
    description: 'Keeps the door schedule the yard works to.',
  },
  {
    slug: 'detention-handling',
    title: 'Detention Handling',
    description: 'Warns the dock before a trailer’s free time runs out.',
  },
  {
    slug: 'document-collection-and-filing',
    title: 'Document Collection & Filing',
    description:
      'We capture every quote, tender, rate con, BOL, POD and invoice as it arrives, and file it against the right shipment automatically.',
  },
  {
    slug: 'tracking-and-status-updates',
    title: 'Tracking & Status Updates',
    description:
      'We get status from carriers so your team stops making check calls, and push clean updates to the people who need them.',
  },
  {
    slug: 'tender-failures',
    title: 'Tender Failures',
    description:
      'When a tender falls through, we re-tender down your routing guide and re-book, before the miss ever reaches your team.',
  },
  {
    slug: 'proactive-load-audits',
    title: 'Proactive Load Audits',
    description:
      'We check every load before pickup and flag at-risk transit early, so problems surface before they turn into penalties.',
  },
  {
    slug: 'claims-processing',
    title: 'Claims Processing',
    description:
      'We gather the documentation, validate the claim, and draft the response for your approval, so claims get filed and recovered.',
  },
  {
    slug: 'scheduling-and-re-scheduling',
    title: 'Scheduling & Re-Scheduling',
    description:
      'We book dock appointments against carrier and facility availability, and when a pickup slips, we re-schedule and notify everyone who needs to know.',
  },
  {
    slug: 'purchase-order-follow-up',
    title: 'Purchase Order Follow-Up',
    description:
      'We find purchase orders due with no appointment, and get the supplier or carrier to book before stock runs short.',
  },
  {
    slug: 'warehouse-customer-service',
    title: 'Warehouse Customer Service',
    description:
      'We answer your warehouse’s status questions and book appointments from an email, using your dock schedule and WMS.',
  },
];

function YardTaskOrchestration() {
  return (
    <PageWrapper>
      <YardTaskOrchestrationGlobalStyle />
      <Navigation theme="dark" />
      <Hero {...hero} />
      <WatchItRunYourFreight {...watchItRun} />
      <HowItWorks {...howItWorks} />
      <RunsOnYourStack />
      <TheRestOfTheLineup cards={lineupCards} />
      <EndCTA agentName="Yard Task Orchestration" />
      <Footer />
    </PageWrapper>
  );
}

export default YardTaskOrchestration;
export { YardTaskOrchestration };
